import React, { useState, useEffect } from "react";
import {
  Box,
  Paper,
  Typography,
  Button,
  Tabs,
  Tab,
  TextField,
  InputAdornment,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Tooltip,
  IconButton,
  Card,
  CardContent,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import {
  Add as AddIcon,
  Search as SearchIcon,
  FilterList as FilterListIcon,
  CheckCircle as CheckCircleIcon,
  Pending as PendingIcon,
  Block as BlockIcon,
  Cancel as CancelIcon,
  PlayArrow as PlayArrowIcon,
  Api as ApiIcon,
  CalendarToday as CalendarIcon,
  Person as PersonIcon,
  PriorityHigh as PriorityHighIcon,
  Visibility as VisibilityIcon,
} from "@mui/icons-material";
import { toast } from "react-toastify";
import { useSelector } from "react-redux";
import type { RootState } from "../../store/Index";
import type { EndpointRequest, RequestStatus, Priority, HttpMethod } from "./types";
import { CreateRequestDialog } from "./components/CreateRequestDialog";
import { ViewRequestDialog } from "./components/ViewRequestDialog";
import { DeveloperStatsComponent } from "./components/DeveloperStats";

const STATUS_COLORS: Record<RequestStatus, "success" | "warning" | "info" | "error" | "default"> = {
  completed: "success",
  in_progress: "info",
  pending: "warning",
  blocked: "error",
  cancelled: "default",
};

const PRIORITY_COLORS: Record<Priority, "error" | "warning" | "info" | "default"> = {
  urgent: "error",
  high: "warning",
  medium: "info",
  low: "default",
};

const METHOD_COLORS: Record<HttpMethod, string> = {
  GET: "#4caf50",
  POST: "#2196f3",
  PUT: "#ff9800",
  PATCH: "#9c27b0",
  DELETE: "#f44336",
};

const EndpointRequestsPage: React.FC = () => {
  const reduxTheme = useSelector((state: RootState) => state.theme);
  const user = useSelector((state: RootState) => state.auth.user);
  const [requests, setRequests] = useState<EndpointRequest[]>([]);
  const [filteredRequests, setFilteredRequests] = useState<EndpointRequest[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState<RequestStatus | "all">("all");
  const [priorityFilter, setPriorityFilter] = useState<Priority | "all">("all");
  const [componentFilter, setComponentFilter] = useState<string>("");
  const [openCreateDialog, setOpenCreateDialog] = useState(false);
  const [openViewDialog, setOpenViewDialog] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState<EndpointRequest | null>(null);
  const [activeTab, setActiveTab] = useState<"all" | "pending" | "completed">("all");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const primaryColor = reduxTheme.primary_colors?.[400] || "#724E41";

  const updateNotificationCount = React.useCallback(() => {
    const pendingCount = requests.filter(
      (r) => r.status === "pending" || r.status === "in_progress"
    ).length;
    localStorage.setItem("endpoint_requests_notification_count", pendingCount.toString());
    // Dispatch custom event to update sidebar
    window.dispatchEvent(new CustomEvent("endpoint-requests-updated"));
  }, [requests]);

  const loadRequests = React.useCallback(() => {
    try {
      const stored = localStorage.getItem("endpoint_requests");
      if (stored) {
        const parsed = JSON.parse(stored);
        setRequests(parsed);
      } else {
        // Initialize with shorter sample data
        const sampleData: EndpointRequest[] = [
          {
            id: "1",
            component: "OLD MUTUAL DASHBOARD",
            task: "Monthly Views Response endpoint",
            lead: "Dancel",
            endpoint: "new endpoint required",
            method: "GET",
            status: "completed",
            priority: "urgent",
            requestDate: "2025-12-12",
            actualCompletionDate: "2025-12-31",
          },
          {
            id: "2",
            component: "PIXEL PILOTS JOBS",
            task: "Modify endpoint to fetch jobs by crew member ID",
            lead: "Camaraderie",
            endpoint: "gigs/pixel-pilot-gigs/",
            method: "GET",
            status: "completed",
            priority: "medium",
            requestDate: "2025-12-18",
            actualCompletionDate: "2025-12-30",
          },
          {
            id: "3",
            component: "MMS",
            task: "Tools and assets endpoint fix",
            lead: "Ruva",
            endpoint: "/api/configurations/tools-assets/",
            method: "PATCH",
            status: "blocked",
            statusCode: 500,
            priority: "urgent",
            requestDate: "2025-12-31",
            challenges: "500 Internal Server Error",
          },
          {
            id: "4",
            component: "OLD MUTUAL REPORTS",
            task: "Stream locations, Views, Utilization endpoints",
            lead: "Dancel",
            endpoint: "new endpoint required",
            method: "GET",
            status: "pending",
            priority: "urgent",
            requestDate: "2025-12-31",
          },
        ];
        setRequests(sampleData);
        localStorage.setItem("endpoint_requests", JSON.stringify(sampleData));
      }
    } catch (error) {
      console.error("Error loading requests:", error);
      toast.error("Failed to load endpoint requests");
    }
  }, []);

  const filterRequests = React.useCallback(() => {
    let filtered = [...requests];

    if (activeTab === "pending") {
      filtered = filtered.filter((r) => r.status === "pending" || r.status === "in_progress");
    } else if (activeTab === "completed") {
      filtered = filtered.filter((r) => r.status === "completed");
    }

    if (statusFilter !== "all") {
      filtered = filtered.filter((r) => r.status === statusFilter);
    }

    if (priorityFilter !== "all") {
      filtered = filtered.filter((r) => r.priority === priorityFilter);
    }

    if (componentFilter) {
      filtered = filtered.filter((r) =>
        r.component.toLowerCase().includes(componentFilter.toLowerCase())
      );
    }

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (r) =>
          r.component.toLowerCase().includes(term) ||
          r.task.toLowerCase().includes(term) ||
          r.lead.toLowerCase().includes(term) ||
          r.endpoint?.toLowerCase().includes(term)
      );
    }

    setFilteredRequests(filtered);
  }, [requests, activeTab, statusFilter, priorityFilter, componentFilter, searchTerm]);

  useEffect(() => {
    loadRequests();
  }, [loadRequests]);

  useEffect(() => {
    if (requests.length > 0) {
      updateNotificationCount();
    }
  }, [requests, updateNotificationCount]);

  useEffect(() => {
    filterRequests();
  }, [filterRequests]);

  const saveRequests = (newRequests: EndpointRequest[]) => {
    try {
      localStorage.setItem("endpoint_requests", JSON.stringify(newRequests));
      setRequests(newRequests);
      updateNotificationCount();
      toast.success("Request saved successfully");
    } catch (error) {
      console.error("Error saving requests:", error);
      toast.error("Failed to save request");
    }
  };

  const handleCreateRequest = (request: Omit<EndpointRequest, "id">) => {
    const newRequest: EndpointRequest = {
      ...request,
      id: Date.now().toString(),
      createdBy: user?.username || "Unknown",
      updatedAt: new Date().toISOString(),
    };
    const updated = [...requests, newRequest];
    saveRequests(updated);
    setOpenCreateDialog(false);
    // Notify backend devs
    toast.info(`New endpoint request created: ${newRequest.component}`);
    
    // Update gamification stats
    window.dispatchEvent(new CustomEvent("endpoint-request-created", { 
      detail: { username: user?.username } 
    }));
  };

  const handleUpdateRequest = (id: string, updates: Partial<EndpointRequest>) => {
    const request = requests.find((r) => r.id === id);
    const updated = requests.map((r) =>
      r.id === id ? { ...r, ...updates, updatedAt: new Date().toISOString() } : r
    );
    saveRequests(updated);
    // Don't close dialog automatically - let user close it
    // setOpenViewDialog(false);
    // setSelectedRequest(null);
    
    // Notify if status changed to completed
    if (updates.status === "completed" && request?.status !== "completed") {
      toast.success(`Endpoint request completed: ${request?.component}`);
    }
  };

  const handleDeleteRequest = (id: string) => {
    const updated = requests.filter((r) => r.id !== id);
    saveRequests(updated);
    setOpenViewDialog(false);
    setSelectedRequest(null);
  };

  const handleViewRequest = (request: EndpointRequest) => {
    setSelectedRequest(request);
    setOpenViewDialog(true);
  };

  const getStatusIcon = (status: RequestStatus) => {
    switch (status) {
      case "completed":
        return <CheckCircleIcon fontSize="small" />;
      case "in_progress":
        return <PlayArrowIcon fontSize="small" />;
      case "pending":
        return <PendingIcon fontSize="small" />;
      case "blocked":
        return <BlockIcon fontSize="small" />;
      case "cancelled":
        return <CancelIcon fontSize="small" />;
    }
  };

  const stats = {
    total: requests.length,
    pending: requests.filter((r) => r.status === "pending" || r.status === "in_progress").length,
    completed: requests.filter((r) => r.status === "completed").length,
    blocked: requests.filter((r) => r.status === "blocked").length,
  };

  const paginatedRequests = filteredRequests.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <Box sx={{ p: 3, backgroundColor: "background.default", minHeight: "100vh" }}>
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 4,
        }}
      >
        <Box>
          <Typography variant="h4" fontWeight="bold" sx={{ mb: 1, color: "text.primary" }}>
            Developers TAB
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage and track API endpoint requests between frontend and backend teams
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setOpenCreateDialog(true)}
          sx={{
            backgroundColor: primaryColor,
            "&:hover": { backgroundColor: primaryColor, opacity: 0.9 },
            boxShadow: 2,
            px: 3,
            py: 1.5,
          }}
        >
          New Request
        </Button>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card
            sx={{
              backgroundColor: primaryColor,
              color: "white",
              boxShadow: 3,
            }}
          >
            <CardContent>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <Box>
                  <Typography variant="body2" sx={{ opacity: 0.9, mb: 1 }}>
                    Total Requests
                  </Typography>
                  <Typography variant="h3" fontWeight="bold">
                    {stats.total}
                  </Typography>
                </Box>
                <ApiIcon sx={{ fontSize: 40, opacity: 0.3 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card sx={{ boxShadow: 3, borderLeft: "4px solid", borderColor: "warning.main" }}>
            <CardContent>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <Box>
                  <Typography color="text.secondary" gutterBottom variant="body2">
                    Pending
                  </Typography>
                  <Typography variant="h3" fontWeight="bold" color="warning.main">
                    {stats.pending}
                  </Typography>
                </Box>
                <PendingIcon sx={{ fontSize: 40, color: "warning.main", opacity: 0.3 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card sx={{ boxShadow: 3, borderLeft: "4px solid", borderColor: "success.main" }}>
            <CardContent>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <Box>
                  <Typography color="text.secondary" gutterBottom variant="body2">
                    Completed
                  </Typography>
                  <Typography variant="h3" fontWeight="bold" color="success.main">
                    {stats.completed}
                  </Typography>
                </Box>
                <CheckCircleIcon sx={{ fontSize: 40, color: "success.main", opacity: 0.3 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card sx={{ boxShadow: 3, borderLeft: "4px solid", borderColor: "error.main" }}>
            <CardContent>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <Box>
                  <Typography color="text.secondary" gutterBottom variant="body2">
                    Blocked
                  </Typography>
                  <Typography variant="h3" fontWeight="bold" color="error.main">
                    {stats.blocked}
                  </Typography>
                </Box>
                <BlockIcon sx={{ fontSize: 40, color: "error.main", opacity: 0.3 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Developer Stats / Gamification */}
      {user?.username && (
        <DeveloperStatsComponent requests={requests} currentUser={user.username} />
      )}

      {/* Tabs */}
      <Paper sx={{ mb: 3, boxShadow: 2 }}>
        <Tabs
          value={activeTab}
          onChange={(_, newValue) => setActiveTab(newValue)}
          sx={{
            borderBottom: 1,
            borderColor: "divider",
            "& .MuiTab-root": {
              textTransform: "none",
              fontWeight: 600,
              minHeight: 64,
            },
          }}
        >
          <Tab label={`All (${stats.total})`} value="all" />
          <Tab label={`Pending (${stats.pending})`} value="pending" />
          <Tab label={`Completed (${stats.completed})`} value="completed" />
        </Tabs>
      </Paper>

      {/* Filters */}
      <Paper sx={{ p: 2.5, mb: 3, boxShadow: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid size={{ xs: 12, md: 4 }}>
            <TextField
              fullWidth
              placeholder="Search by component, task, lead, or endpoint..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
              size="small"
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 2 }}>
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                label="Status"
                onChange={(e) => setStatusFilter(e.target.value as RequestStatus | "all")}
              >
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="pending">Pending</MenuItem>
                <MenuItem value="in_progress">In Progress</MenuItem>
                <MenuItem value="completed">Completed</MenuItem>
                <MenuItem value="blocked">Blocked</MenuItem>
                <MenuItem value="cancelled">Cancelled</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 2 }}>
            <FormControl fullWidth size="small">
              <InputLabel>Priority</InputLabel>
              <Select
                value={priorityFilter}
                label="Priority"
                onChange={(e) => setPriorityFilter(e.target.value as Priority | "all")}
              >
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="urgent">Urgent</MenuItem>
                <MenuItem value="high">High</MenuItem>
                <MenuItem value="medium">Medium</MenuItem>
                <MenuItem value="low">Low</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 2 }}>
            <TextField
              fullWidth
              placeholder="Component"
              value={componentFilter}
              onChange={(e) => setComponentFilter(e.target.value)}
              size="small"
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 2 }}>
            <Button
              fullWidth
              variant="outlined"
              startIcon={<FilterListIcon />}
              onClick={() => {
                setStatusFilter("all");
                setPriorityFilter("all");
                setComponentFilter("");
                setSearchTerm("");
              }}
            >
              Clear
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Table */}
      <Paper sx={{ boxShadow: 3, overflow: "hidden" }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: "grey.100" }}>
                <TableCell sx={{ fontWeight: 700 }}>Component</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Task</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Lead</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Endpoint</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Priority</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Date</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedRequests.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">No requests found</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                paginatedRequests.map((request) => (
                  <TableRow
                    key={request.id}
                    hover
                    onClick={() => handleViewRequest(request)}
                    sx={{
                      "&:hover": { backgroundColor: "action.hover", cursor: "pointer" },
                    }}
                  >
                    <TableCell>
                      <Typography variant="body2" fontWeight="medium">
                        {request.component}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Tooltip title={request.task}>
                        <Typography
                          variant="body2"
                          sx={{
                            maxWidth: 300,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {request.task}
                        </Typography>
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      <Chip
                        icon={<PersonIcon />}
                        label={request.lead}
                        size="small"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        {request.method && (
                          <Chip
                            label={request.method}
                            size="small"
                            sx={{
                              backgroundColor: METHOD_COLORS[request.method as HttpMethod] || "#666",
                              color: "white",
                              fontWeight: "bold",
                              fontSize: "0.7rem",
                              height: 22,
                            }}
                          />
                        )}
                        <Typography
                          variant="body2"
                          sx={{
                            fontFamily: "monospace",
                            fontSize: "0.75rem",
                            maxWidth: 150,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                        >
                          {request.endpoint || "N/A"}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        icon={getStatusIcon(request.status)}
                        label={request.status.replace("_", " ").toUpperCase()}
                        color={STATUS_COLORS[request.status]}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        icon={<PriorityHighIcon />}
                        label={request.priority.toUpperCase()}
                        color={PRIORITY_COLORS[request.priority]}
                        size="small"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                        <CalendarIcon fontSize="small" sx={{ opacity: 0.7 }} />
                        <Typography variant="body2" fontSize="0.875rem">
                          {new Date(request.requestDate).toLocaleDateString()}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell align="center">
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleViewRequest(request);
                        }}
                        color="primary"
                      >
                        <VisibilityIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={filteredRequests.length}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[5, 10, 25, 50]}
        />
      </Paper>

      {/* Create Dialog */}
      <CreateRequestDialog
        open={openCreateDialog}
        onClose={() => setOpenCreateDialog(false)}
        onSubmit={handleCreateRequest}
      />

      {/* View/Edit Dialog */}
      {selectedRequest && (
        <ViewRequestDialog
          open={openViewDialog}
          request={selectedRequest}
          onClose={() => {
            setOpenViewDialog(false);
            setSelectedRequest(null);
          }}
          onUpdate={handleUpdateRequest}
          onDelete={handleDeleteRequest}
        />
      )}
    </Box>
  );
};

export default EndpointRequestsPage;
